import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { getItemFromStorage, validateToken, signOut } from '@dtna/dvs-core-dcp-client-utils/lib/utils/browserStorage';
import AppConstants from '@dtna/dvs-core-dcp-client-utils/lib/constants/storage';
import { verify } from '@dtna/dvs-core-dcp-client-utils/lib/utils/jwt';
import { BASE_URL } from '../constants/proxyApi';

const baseURL = BASE_URL;

/**
 * Axios request wrapper for proxy api.
 * @param config Partial<AxiosRequestConfig>
 * @returns Promise<AxiosResponse<T>>
 */
const doRequest = <T>(config?: Partial<AxiosRequestConfig>): Promise<AxiosResponse<T>> => {
  const r = axios.request<T>({ ...config, baseURL });
  return r;
};

/**
 * open request to non-protected endpoint.
 * @param config Partial<AxiosRequestConfig>
 * @returns Promise<AxiosResponse<T>>
 */
const request = async <T>(config?: Partial<AxiosRequestConfig>): Promise<AxiosResponse<T>> => {
  const headers = config?.headers ? config.headers : {};
  const result = doRequest<T>({
    ...config,
    headers: {
      ...headers,
    },
  });
  return result;
};

const getBearerToken = async () => {
  try {
    if (process.env.REACT_APP_AUTH_TOKEN) return process.env.REACT_APP_AUTH_TOKEN;
    const token = getItemFromStorage(AppConstants.LOCAL_STORAGE.TELEMATICS.TOKEN) as string;
    if (!token) throw new Error('token not found');

    return new Promise((resolve, reject) => {
      verify(token, async (error, decodedToken) => {
        if (error) reject(error.message);
        const newDecodedToken = await validateToken(decodedToken);
        resolve(newDecodedToken.bearerToken);
      });
    });
  } catch (error) {
    signOut();
    throw new Error(error as any);
  }
};

/**
 * request from logged in user to protected endpoint.
 * @param config Partial<AxiosRequestConfig>
 * @returns Promise<AxiosResponse<T>>
 */
const protectedRequest = async <T>(config?: Partial<AxiosRequestConfig>) => {
  const headers = config?.headers ? config.headers : {};
  const bearerToken = await getBearerToken();
  const result = doRequest<T>({
    ...config,
    headers: {
      ...headers,
      Authorization: `Bearer ${bearerToken}`,
    },
  });
  return result;
};

const proxyApi = {
  baseURL,
  protectedRequest,
  request,
};

export default proxyApi;
